
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  getDashboardStats,
  getCategoryDistribution,
  getExpiryTrend,
  getInventoryHealth,
  getLocationDistribution,
  DashboardStats,
  CategoryDistribution,
  ExpiryTrend,
  InventoryHealth,
  LocationDistribution
} from "@/utils/dashboardUtils";

export const useDashboardData = (alertWindow: number = 7) => {
  // Get dashboard statistics
  const statsQuery = useQuery({
    queryKey: ['dashboardStats', alertWindow],
    queryFn: () => getDashboardStats(alertWindow),
    meta: {
      onError: (error: any) => {
        toast.error("Failed to load dashboard statistics", {
          position: "bottom-right",
          dismissible: true,
        });
        console.error("Error fetching dashboard stats:", error);
      }
    }
  });

  // Get category distribution
  const categoryQuery = useQuery({
    queryKey: ['categoryDistribution'],
    queryFn: getCategoryDistribution,
    meta: {
      onError: (error: any) => {
        toast.error("Failed to load category distribution", {
          position: "bottom-right",
          dismissible: true,
        });
        console.error("Error fetching category distribution:", error);
      }
    }
  });

  // Get expiry trend
  const trendQuery = useQuery({
    queryKey: ['expiryTrend'],
    queryFn: getExpiryTrend,
    meta: {
      onError: (error: any) => {
        toast.error("Failed to load expiry trend", {
          position: "bottom-right",
          dismissible: true,
        });
        console.error("Error fetching expiry trend:", error);
      }
    }
  });

  // Get inventory health
  const healthQuery = useQuery({
    queryKey: ['inventoryHealth', alertWindow],
    queryFn: () => getInventoryHealth(alertWindow),
    meta: {
      onError: (error: any) => {
        toast.error("Failed to load inventory health", {
          position: "bottom-right",
          dismissible: true,
        });
        console.error("Error fetching inventory health:", error);
      }
    }
  });

  // Get location distribution
  const locationQuery = useQuery({
    queryKey: ['locationDistribution'],
    queryFn: getLocationDistribution,
    meta: {
      onError: (error: any) => {
        toast.error("Failed to load location distribution", {
          position: "bottom-right",
          dismissible: true,
        });
        console.error("Error fetching location distribution:", error);
      }
    }
  });

  const isLoading = 
    statsQuery.isLoading || 
    categoryQuery.isLoading || 
    trendQuery.isLoading ||
    healthQuery.isLoading ||
    locationQuery.isLoading;

  const hasErrors =
    statsQuery.isError ||
    categoryQuery.isError ||
    trendQuery.isError ||
    healthQuery.isError ||
    locationQuery.isError;
    
  // Calculate total counts for quick reference
  const totals = {
    totalItems: statsQuery.data?.totalInventory || 0,
    totalExpiringSoon: statsQuery.data?.expiringSoon || 0,
    totalExpired: statsQuery.data?.recentlyExpired || 0,
    averageShelfLife: statsQuery.data?.averageShelfLife || 0
  };
  
  // Calculate percentages for quick reference
  const percentages = {
    expiringSoonPercent: totals.totalItems > 0 ? Math.round((totals.totalExpiringSoon / totals.totalItems) * 100) : 0,
    expiredPercent: totals.totalItems > 0 ? Math.round((totals.totalExpired / totals.totalItems) * 100) : 0,
    healthyPercent: totals.totalItems > 0 ? 100 - (
      Math.round((totals.totalExpiringSoon / totals.totalItems) * 100) + 
      Math.round((totals.totalExpired / totals.totalItems) * 100)
    ) : 0
  };

  // Function to refetch all data
  const refetchAll = () => {
    statsQuery.refetch();
    categoryQuery.refetch();
    trendQuery.refetch();
    healthQuery.refetch();
    locationQuery.refetch();
    toast.success("Dashboard data refreshed", {
      position: "bottom-right",
      dismissible: true,
    });
  };

  return {
    stats: statsQuery.data || { expiringSoon: 0, recentlyExpired: 0, totalInventory: 0, averageShelfLife: 0 },
    categoryDistribution: categoryQuery.data || [],
    expiryTrend: trendQuery.data || [],
    inventoryHealth: healthQuery.data || [],
    locationDistribution: locationQuery.data || [],
    totals,
    percentages,
    isLoading,
    hasErrors,
    refetchAll
  };
};
